import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, Building2, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import CompetencyMatrixBuilder from "../components/CompetencyMatrixBuilder";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_MATRIX = [
  { name: "Technical Skills", weight: 25 },
  { name: "Experience Relevance", weight: 20 },
  { name: "Education & Certifications", weight: 15 },
  { name: "Leadership & Management", weight: 15 },
  { name: "Communication Skills", weight: 10 },
  { name: "Cultural Fit", weight: 15 },
];

export default function NewJob() {
  const [step, setStep] = useState(1);
  const [organization, setOrganization] = useState("");
  const [title, setTitle] = useState("");
  const [jdText, setJdText] = useState("");
  const [matrix, setMatrix] = useState(DEFAULT_MATRIX);
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const goToStep2 = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const total = matrix.reduce((s, c) => s + Number(c.weight || 0), 0);
    if (total !== 100) {
      toast.error("Competency weights must total exactly 100%");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/jobs", {
        organization,
        title,
        jd_text: jdText,
        competency_matrix: matrix,
        eval_instructions: instructions,
      });
      toast.success("Job created!");
      navigate(`/app/jobs/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
      <div className="mb-8 flex items-center gap-3" data-testid="new-job-step-indicator">
        <StepDot active={step === 1} done={step > 1} number={1} label="Details" />
        <div className={`h-px flex-1 ${step > 1 ? "bg-primary" : "bg-border"}`} />
        <StepDot active={step === 2} done={false} number={2} label="Job Description" />
      </div>

      {step === 1 ? (
        <form onSubmit={goToStep2} className="space-y-6">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Who is this job for?</h1>
            <p className="mt-1 text-sm text-muted-foreground">Tell us the organization and role, then we'll capture the job description.</p>
          </div>

          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> Organization
            </label>
            <Input
              required
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="e.g. Acme Corp"
              data-testid="job-organization-input"
            />
          </div>
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
              <Briefcase className="h-3.5 w-3.5 text-muted-foreground" /> Job Role / Title
            </label>
            <Input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Senior Backend Engineer"
              data-testid="job-title-input"
            />
          </div>

          <Button type="submit" size="lg" data-testid="job-step1-continue-btn">
            Continue <ArrowRight className="h-4 w-4" />
          </Button>
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">at {organization} — paste the job description and set up scoring</p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">Job Description</label>
            <Textarea
              required
              autoFocus
              value={jdText}
              onChange={(e) => setJdText(e.target.value)}
              placeholder="Paste the complete job description here..."
              className="min-h-[180px]"
              data-testid="job-jd-textarea"
            />
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <label className="mb-3 block text-sm font-medium">Competency Matrix</label>
            <CompetencyMatrixBuilder matrix={matrix} setMatrix={setMatrix} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Evaluation Instructions (optional)</label>
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Prioritize manufacturing experience, Do not assume missing skills..."
              data-testid="job-instructions-textarea"
            />
          </div>

          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="lg" onClick={() => setStep(1)} data-testid="job-step2-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button type="submit" disabled={loading} size="lg" data-testid="submit-job-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Job"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function StepDot({ active, done, number, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors duration-200 ${
          active || done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        }`}
      >
        {number}
      </div>
      <span className={`text-sm font-medium ${active ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );
}
